import type { DateRangeValue } from '../components/filters/types';

const PRESET_DAYS: Record<string, number> = { '7': 7, '14': 14, '30': 30, '60': 60, '90': 90 };
const DEFAULT_DAYS = 14;

// ALX finishes yesterday's data in the afternoon Israel time, so a preset ends at the newest day the
// data covers rather than at the viewer's today. A two-day anchor needs alx-sdk >= 1.14.4.
const DATA_READY_HOUR_IL = 17;
const REPORTING_TIME_ZONE = 'Asia/Jerusalem';
const PRE_TICK_LAG_DAYS = 2;
const POST_TICK_LAG_DAYS = 1;

function isoDate(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

/** Today's date and hour in Israel, read off the viewer's clock. `en-CA` formats as YYYY-MM-DD. */
function israelNow(now: Date): { date: string; hour: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORTING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const value = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return {
    date: `${value('year')}-${value('month')}-${value('day')}`,
    // A midnight hour formats as '24' in some ICU versions; both readings are before the tick.
    hour: Number(value('hour')) % 24,
  };
}

/** The newest day ALX has settled data for, as of `now`. */
export function latestSettledDate(now: Date): string {
  const { date, hour } = israelNow(now);
  const daysBack = hour < DATA_READY_HOUR_IL ? PRE_TICK_LAG_DAYS : POST_TICK_LAG_DAYS;
  const anchor = new Date(`${date}T00:00:00Z`);
  anchor.setUTCDate(anchor.getUTCDate() - daysBack);
  return isoDate(anchor);
}

/** Israel's whole-hour offset (+2 winter, +3 summer), read off the viewer's ICU data. */
function reportingOffsetHours(now: Date): number {
  const offset = new Intl.DateTimeFormat('en-US', {
    timeZone: REPORTING_TIME_ZONE,
    timeZoneName: 'longOffset',
  })
    .formatToParts(now)
    .find((part) => part.type === 'timeZoneName')?.value;
  return Number(offset?.replace('GMT', '').split(':')[0]) || 0;
}

/** When the cutoff next moves, in UTC, for the date control's one-line note. */
export function nextUpdateLabel(now: Date): string {
  const { date, hour } = israelNow(now);
  const tick = new Date(`${date}T00:00:00Z`);
  if (hour >= DATA_READY_HOUR_IL) tick.setUTCDate(tick.getUTCDate() + 1);
  // The tick is 17:00 Israel time; the offset at `now` stands in for the offset at the tick itself.
  tick.setUTCHours(DATA_READY_HOUR_IL - reportingOffsetHours(now));
  const when = isoDate(tick) === isoDate(now) ? 'today' : 'tomorrow';
  return `${when} at ${String(tick.getUTCHours()).padStart(2, '0')}:00 UTC`;
}

export function dateWindowFromPreset(preset: string, now: Date): [string, string] {
  const days = PRESET_DAYS[preset] ?? DEFAULT_DAYS;
  const end = latestSettledDate(now);
  const start = new Date(`${end}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - days);
  return [isoDate(start), end];
}

export function dateWindowFromRange(range: DateRangeValue, now: Date): [string, string] {
  // ISO dates sort as strings; a range picked end-first still means the days between them.
  if (range.mode === 'custom' && range.from && range.to) {
    return range.from <= range.to ? [range.from, range.to] : [range.to, range.from];
  }
  return dateWindowFromPreset(range.preset, now);
}

export function formatDuration(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) return null;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
